/**
 * The CSS for a rendered maker page. Returned as a string so it can be inlined
 * into the SSR <head> AND injected into the editor preview pane — same CSS,
 * same markup, identical result.
 */

import type { Font } from "./fonts"
import type { Theme } from "./themes"
import { themeCssVars } from "./themes"

/**
 * @param scope CSS selector the variables + rules are scoped to. Use ":root"
 *   for full-page SSR, or a container class (e.g. ".ms-preview") for the editor
 *   preview so it doesn't leak into the surrounding app chrome.
 */
export function pageStyles(theme: Theme, font: Font, scope = ":root"): string {
  const root = scope === ":root" ? ":root" : scope
  const wrap = scope === ":root" ? "" : `${scope} `
  return `
${root}{${themeCssVars(theme)};--ms-font:${font.stack}}
${wrap}*{box-sizing:border-box}
${wrap}.ms-root{background:var(--ms-page-bg);color:var(--ms-text);font-family:var(--ms-font);min-height:100%;margin:0;padding:0;-webkit-font-smoothing:antialiased}
${wrap}.ms-container{max-width:560px;margin:0 auto;padding:48px 20px 80px}
${wrap}.ms-profile{display:flex;flex-direction:column;align-items:flex-start;gap:14px}
${wrap}.ms-avatar{width:72px;height:72px;border-radius:9999px;object-fit:cover;background:var(--ms-border)}
${wrap}.ms-name{font-size:24px;font-weight:800;line-height:1.15;margin:0}
${wrap}.ms-location{display:inline-flex;align-items:center;gap:6px;color:var(--ms-muted);font-size:14px}
${wrap}.ms-bio{color:var(--ms-text);font-size:16px;line-height:1.55;margin:2px 0 0}
${wrap}.ms-bio a{color:var(--ms-accent);text-decoration:none}
${wrap}.ms-socials{display:flex;flex-wrap:wrap;gap:14px;margin-top:4px}
${wrap}.ms-socials a{color:var(--ms-muted);display:inline-flex;transition:color .15s}
${wrap}.ms-socials a:hover{color:var(--ms-accent)}
${wrap}.ms-subscribe{margin-top:22px;width:100%}
${wrap}.ms-subscribe-headline{font-size:14px;color:var(--ms-muted);margin:0 0 8px}
${wrap}.ms-subscribe-row{display:flex;gap:8px}
${wrap}.ms-subscribe input{flex:1;min-width:0;padding:11px 14px;border-radius:10px;border:1px solid var(--ms-border);background:var(--ms-card-bg);color:var(--ms-text);font-size:14px;font-family:inherit}
${wrap}.ms-subscribe button{padding:11px 18px;border-radius:10px;border:0;background:var(--ms-accent);color:var(--ms-accent-text);font-weight:600;font-size:14px;cursor:pointer;font-family:inherit}
${wrap}.ms-startups{display:flex;flex-direction:column;gap:12px;margin-top:34px}
${wrap}.ms-card{display:flex;align-items:center;gap:14px;padding:16px;border-radius:14px;background:var(--ms-card-bg);border:1px solid var(--ms-border);text-decoration:none;color:inherit;transition:transform .12s,border-color .15s}
${wrap}.ms-card:not(.ms-card-static):hover{transform:translateY(-1px);border-color:var(--ms-accent)}
${wrap}.ms-card-static{cursor:default}
${wrap}.ms-card-icon{width:40px;height:40px;border-radius:10px;object-fit:cover;flex-shrink:0;background:var(--ms-border)}
${wrap}.ms-card-body{min-width:0;flex:1}
${wrap}.ms-card-title-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
${wrap}.ms-card-name{font-weight:700;font-size:16px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
${wrap}.ms-card-meta{display:flex;align-items:center;justify-content:flex-end;gap:8px;flex-shrink:0}
${wrap}.ms-card-revenue{font-size:13px;font-weight:700;color:var(--ms-text);white-space:nowrap}
${wrap}.ms-card-tagline{color:var(--ms-muted);font-size:14px;line-height:1.4;margin-top:2px;white-space:normal;overflow-wrap:anywhere}
${wrap}.ms-badge{font-size:11px;font-weight:600;padding:2px 8px;border-radius:9999px;background:var(--ms-page-bg);border:1px solid var(--ms-border);color:var(--ms-muted)}
${wrap}.ms-status{font-size:11px;font-weight:700;line-height:1;padding:7px 10px;border-radius:9999px;color:#fff;background:#64748b;white-space:nowrap}
${wrap}.ms-status-active,${wrap}.ms-status-live{background:#22c55e}
${wrap}.ms-status-building{background:#3b82f6}
${wrap}.ms-status-paused,${wrap}.ms-status-on-hold{background:#f59e0b}
${wrap}.ms-status-deprecated,${wrap}.ms-status-discontinued,${wrap}.ms-status-shut-down{background:#f87171}
${wrap}.ms-status-acquired{background:#8b5cf6}
${wrap}.ms-footer{display:flex;align-items:center;justify-content:center;gap:7px;margin-top:44px;text-align:center;font-size:12px;color:var(--ms-muted)}
${wrap}.ms-footer-brand{display:inline-flex;align-items:center;gap:7px;color:var(--ms-text);text-decoration:none}
${wrap}.ms-footer-brand strong{font-size:12px;font-weight:800;letter-spacing:-.02em}
${wrap}.ms-brand-mark{position:relative;display:inline-block;width:19px;height:16px;flex:0 0 auto}
${wrap}.ms-brand-mark i{position:absolute;left:0;width:18px;height:6px;border:1.5px solid var(--ms-text);border-radius:2px;transform:skewY(-7deg)}
${wrap}.ms-brand-mark i:nth-child(1){top:0;background:var(--ms-accent);border-color:var(--ms-accent)}
${wrap}.ms-brand-mark i:nth-child(2){top:5px;left:1.5px;background:var(--ms-page-bg)}
${wrap}.ms-brand-mark i:nth-child(3){top:10px;left:3px;background:var(--ms-page-bg)}
${wrap}.ms-footer-brand:hover{color:var(--ms-accent)}
@media (min-width:860px){
${wrap}.ms-container{max-width:1080px;min-height:100vh;padding:76px 56px 52px;display:grid;grid-template-columns:minmax(260px,360px) minmax(0,1fr);column-gap:104px;align-items:start}
${wrap}.ms-profile{grid-column:1;position:sticky;top:76px;gap:16px}
${wrap}.ms-avatar{width:200px;height:200px}
${wrap}.ms-name{font-size:42px;letter-spacing:-.04em}
${wrap}.ms-bio{font-size:17px;max-width:330px}
${wrap}.ms-subscribe{max-width:360px}
${wrap}.ms-startups{grid-column:2;margin-top:26px;gap:14px}
${wrap}.ms-card{padding:18px 20px;border-radius:18px}
${wrap}.ms-card-icon{width:44px;height:44px}
${wrap}.ms-footer{grid-column:1/-1;margin-top:54px}
}
`.trim()
}
